import React from 'react';
import { Flag, Chip, Button } from './primitives.jsx';
import { buildSlots, pickCurrentSlot, SLOT_ORDER } from './matchdaySlot.js';

// ============================================================================
// Predictor (Super 6 scoring — match predictions + winner pick)
// ============================================================================

export const Predictor = ({ pool, myTeam }) => {
  const [name, setName] = React.useState(myTeam?.assigned_to || '');
  const [predictions, setPredictions] = React.useState({});      // matchId → { scoreA_pred, scoreB_pred, points }
  const [drafts, setDrafts] = React.useState({});                // matchId → { a, b } unsaved edits
  const [winnerPick, setWinnerPick] = React.useState('');
  const [winnerPickPoints, setWinnerPickPoints] = React.useState(null);
  const [savingMatch, setSavingMatch] = React.useState(null);
  const [savedFlash, setSavedFlash] = React.useState(null);
  const [savingWinner, setSavingWinner] = React.useState(false);
  const [error, setError] = React.useState('');

  const trimmedName = name.trim();
  const ready = trimmedName.length > 0;

  // Refresh predictions whenever the active name changes.
  React.useEffect(() => {
    if (!ready) { setPredictions({}); setWinnerPick(''); return; }
    const q = encodeURIComponent(trimmedName);
    Promise.all([
      fetch(`/api/pool/${pool.id}/predictions?name=${q}`).then(r => r.json()),
      fetch(`/api/pool/${pool.id}/picks?name=${q}`).then(r => r.json()),
    ]).then(([p, pk]) => {
      const map = {};
      (p.predictions || []).forEach(row => { map[row.match_id] = row; });
      setPredictions(map);
      const winner = (pk.picks || []).find(x => x.pick_type === 'winner');
      setWinnerPick(winner?.pick_value || '');
      setWinnerPickPoints(winner?.points ?? null);
    }).catch(() => {});
  }, [pool.id, trimmedName, ready]);

  const matches = React.useMemo(() => {
    const list = [...(pool.matches || [])];
    if (pool.bracket) {
      ['r32', 'r16', 'qf', 'sf', 'f'].forEach(r => {
        (pool.bracket[r] || []).forEach(m => list.push({ ...m, group: r.toUpperCase() }));
      });
    }
    return list;
  }, [pool.matches, pool.bracket]);

  const teamOf = (id) => pool.teams.find(t => t.id === id);

  const saveOne = async (matchId, a, b) => {
    if (!ready) { setError('Type your name first.'); return; }
    if (a === '' || b === '' || Number.isNaN(+a) || Number.isNaN(+b)) {
      setError('Enter both scores.');
      return;
    }
    setError(''); setSavingMatch(matchId);
    try {
      const res = await fetch(`/api/pool/${pool.id}/predictions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmedName, matchId, scoreA: +a, scoreB: +b }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Save failed.'); return; }
      setPredictions(prev => ({
        ...prev,
        [matchId]: { match_id: matchId, scoreA_pred: +a, scoreB_pred: +b, points: null },
      }));
      setDrafts(prev => { const next = { ...prev }; delete next[matchId]; return next; });
      setSavedFlash(matchId);
      setTimeout(() => setSavedFlash(s => (s === matchId ? null : s)), 1200);
    } catch {
      setError('Network error.');
    } finally {
      setSavingMatch(null);
    }
  };

  const saveWinner = async () => {
    if (!ready) { setError('Type your name first.'); return; }
    if (!winnerPick) { setError('Pick a team.'); return; }
    setError(''); setSavingWinner(true);
    try {
      const res = await fetch(`/api/pool/${pool.id}/picks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmedName, pickType: 'winner', pickValue: winnerPick }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Save failed.'); return; }
    } catch {
      setError('Network error.');
    } finally {
      setSavingWinner(false);
    }
  };

  const slots = React.useMemo(() => buildSlots(matches), [matches]);

  const winnerLocked = pool.stage !== 'group';

  // Empty-state styling for the name field. When the user hasn't entered a
  // name yet (i.e. a guest who scanned the QR and tapped Predictor), the
  // whole card switches to a high-contrast prompt — otherwise the score
  // inputs below sit disabled with no obvious explanation.
  const needsName = !ready;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Name field */}
      <div style={{
        background: needsName ? 'var(--blue)' : 'var(--paper-warm)',
        color: needsName ? 'var(--paper)' : 'var(--ink)',
        border: '1.5px solid ' + (needsName ? 'var(--blue)' : 'var(--hairline-strong)'),
        borderRadius: 12, padding: 20, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
        transition: 'background 200ms, border-color 200ms',
      }}>
        <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 220 }}>
          <span style={{
            fontFamily: 'var(--font-display)',
            fontSize: needsName ? 16 : 12,
            letterSpacing: needsName ? '0.04em' : '0.14em',
            textTransform: 'uppercase',
            color: needsName ? 'var(--paper)' : 'var(--ink-soft)',
          }}>
            {needsName ? 'ENTER YOUR NAME TO START PREDICTING →' : 'PREDICTING AS'}
          </span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={needsName ? 'Type the name you drew with' : 'Your name'}
            autoFocus={needsName}
            style={{
              border: '1.5px solid ' + (needsName ? 'var(--paper)' : 'var(--hairline-strong)'),
              borderRadius: 8,
              padding: '12px 14px', fontFamily: 'var(--font-body)', fontSize: 16,
              background: 'var(--paper)', color: 'var(--ink)',
            }}
          />
        </label>
        <div style={{
          fontFamily: 'var(--font-mono)', fontSize: 12,
          color: needsName ? 'rgba(255,255,255,0.85)' : 'var(--ink-soft)',
          maxWidth: 320,
        }}>
          {needsName
            ? 'Predictions are tied to the name you drew with. Both admins and players can predict — just enter your name above.'
            : 'Predictions lock once a match kicks off. Super 6 scoring: exact 5 · result+GD 3 · result 1.'}
        </div>
      </div>

      {error && (
        <div style={{ background: 'var(--danger-pale, #fde2e2)', color: 'var(--danger)', padding: 12, borderRadius: 8, fontSize: 14 }}>
          {error}
        </div>
      )}

      {/* Winner pick */}
      <div style={{
        background: 'var(--ink)', color: 'var(--paper)', borderRadius: 12,
        padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
      }}>
        <div style={{ flex: '1 1 220px' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold)' }}>
            TOURNAMENT WINNER · 50PT
          </div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, textTransform: 'uppercase', marginTop: 4, letterSpacing: '-0.005em' }}>
            {winnerPick
              ? `Backing ${teamOf(winnerPick)?.country || winnerPick}`
              : 'Pick the eventual winner'}
            {winnerPickPoints != null && (
              <span style={{ marginLeft: 12, fontSize: 14, color: 'var(--gold)' }}>
                · {winnerPickPoints} PT
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginTop: 4, fontFamily: 'var(--font-mono)' }}>
            {winnerLocked ? 'LOCKED — BRACKET STAGE' : 'LOCKS WHEN BRACKET STARTS'}
          </div>
        </div>
        <select
          value={winnerPick}
          onChange={(e) => setWinnerPick(e.target.value)}
          disabled={winnerLocked || !ready}
          style={{
            background: 'var(--paper)', color: 'var(--ink)',
            border: '1.5px solid var(--paper)', borderRadius: 8,
            padding: '10px 14px', fontSize: 15, minWidth: 200,
            opacity: winnerLocked ? 0.5 : 1,
          }}
        >
          <option value="">— Select team —</option>
          {pool.teams
            .slice()
            .sort((a, b) => a.country.localeCompare(b.country))
            .map(t => (
              <option key={t.id} value={t.id}>
                {t.country} (Grp {t.group})
              </option>
            ))}
        </select>
        <Button variant="onInk" onClick={saveWinner} disabled={winnerLocked || !ready || savingWinner}>
          {savingWinner ? 'SAVING…' : 'SAVE WINNER'}
        </Button>
      </div>

      <MatchdayWindow
        slots={slots}
        predictions={predictions}
        drafts={drafts}
        savingMatch={savingMatch}
        savedFlash={savedFlash}
        ready={ready}
        teamOf={teamOf}
        onDraftChange={(matchId, side, v) => setDrafts(prev => ({
          ...prev,
          [matchId]: { ...(prev[matchId] || {}), [side]: v },
        }))}
        onSave={(matchId) => {
          const d = drafts[matchId] || {};
          const p = predictions[matchId] || {};
          const a = d.a ?? p.scoreA_pred ?? '';
          const b = d.b ?? p.scoreB_pred ?? '';
          saveOne(matchId, a, b);
        }}
      />
    </div>
  );
};

const MatchdayWindow = ({ slots, predictions, drafts, savingMatch, savedFlash, ready, teamOf, onDraftChange, onSave }) => {
  const initialIndex = React.useMemo(() => {
    const current = pickCurrentSlot(slots, Date.now());
    return Math.max(0, SLOT_ORDER.indexOf(current.key));
  }, [slots]);
  const [index, setIndex] = React.useState(initialIndex);

  // Clamp index if slots change (e.g. bracket fills in later)
  const safeIndex = Math.min(index, slots.length - 1);
  const slot = slots[safeIndex];

  const statusFor = (s) => {
    if (s.matches.length === 0) return 'UPCOMING';
    const allDone = s.matches.every(m => m.status === 'completed');
    if (allDone) return 'COMPLETED';
    const anyStarted = s.matches.some(m => m.status !== 'scheduled'
      || (m.kickoff && Date.parse(m.kickoff) <= Date.now()));
    return anyStarted ? 'NOW' : 'UPCOMING';
  };
  const status = statusFor(slot);

  return (
    <div style={{
      background: 'var(--paper)', border: '1.5px solid var(--hairline-strong)',
      borderRadius: 12, padding: 20,
    }}>
      {/* Header: dropdown selector (mobile-friendly — was prev/next chevrons) */}
      <label style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
      }}>
        <span style={{
          fontFamily: 'var(--font-display)', fontSize: 12,
          letterSpacing: '0.14em', textTransform: 'uppercase',
          color: 'var(--ink-soft)', flexShrink: 0,
        }}>MATCHDAY</span>
        <select
          value={slot.key}
          onChange={(e) => {
            const newIdx = SLOT_ORDER.indexOf(e.target.value);
            if (newIdx >= 0) setIndex(newIdx);
          }}
          style={{
            flex: 1, minWidth: 0,
            padding: '12px 14px', borderRadius: 8,
            border: '1.5px solid var(--hairline-strong)',
            background: 'var(--paper)', color: 'var(--blue)',
            fontFamily: 'var(--font-display)', fontSize: 16,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >
          {slots.map(s => (
            <option key={s.key} value={s.key}>
              {s.label} · {statusFor(s)}
            </option>
          ))}
        </select>
      </label>

      {/* Dot indicator */}
      <div style={{
        display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 20,
      }}>
        {slots.map((s, i) => (
          <span key={s.key} style={{
            width: 8, height: 8, borderRadius: '50%',
            background: i === safeIndex ? 'var(--blue)' :
                        s.matches.every(m => m.status === 'completed') && s.matches.length > 0
                          ? 'var(--ink-mute)' : 'var(--hairline-strong)',
          }} />
        ))}
      </div>

      {/* Match rows for the current slot */}
      {slot.matches.length === 0 ? (
        <div style={{
          padding: 24, textAlign: 'center',
          fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--ink-soft)',
        }}>
          Matches will appear here once the bracket is set.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {slot.matches.map(m => (
            <MatchRow
              key={m.id}
              match={m}
              teamA={teamOf(m.teamA_id)}
              teamB={teamOf(m.teamB_id)}
              prediction={predictions[m.id]}
              draft={drafts[m.id]}
              onDraftChange={(side, v) => onDraftChange(m.id, side, v)}
              onSave={() => onSave(m.id)}
              saving={savingMatch === m.id}
              justSaved={savedFlash === m.id}
              ready={ready}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const MatchRow = ({ match, teamA, teamB, prediction, draft, onDraftChange, onSave, saving, justSaved, ready }) => {
  // Locked once a real result is in (status) OR the match has kicked off (time).
  const locked = match.status !== 'scheduled'
    || (match.kickoff && Date.parse(match.kickoff) <= Date.now());
  const a = draft?.a ?? prediction?.scoreA_pred ?? '';
  const b = draft?.b ?? prediction?.scoreB_pred ?? '';
  const earned = prediction?.points;

  const teamSide = (team) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      {team ? <Flag teamId={team.id} size={28} radius={3} /> : <span style={{ width: 28, height: 21, background: 'var(--hairline)', borderRadius: 3 }} />}
      <span style={{
        fontFamily: 'var(--font-display)', fontSize: 14, textTransform: 'uppercase',
        letterSpacing: '0.04em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>{team?.country || 'TBD'}</span>
    </div>
  );

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr) auto',
      gap: 12, alignItems: 'center',
      padding: '10px 12px', borderRadius: 8,
      background: locked ? 'var(--paper-warm)' : 'var(--paper)',
      border: `1px solid ${justSaved ? 'var(--success, #2eab57)' : 'var(--hairline)'}`,
      transition: 'border 200ms',
    }}>
      {teamSide(teamA)}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {locked ? (
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700 }}>
            {match.scoreA ?? '–'} : {match.scoreB ?? '–'}
          </span>
        ) : (
          <>
            <input
              type="number" min="0" max="30"
              value={a}
              onChange={(e) => onDraftChange('a', e.target.value)}
              disabled={!ready}
              style={inputStyle}
            />
            <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-soft)' }}>:</span>
            <input
              type="number" min="0" max="30"
              value={b}
              onChange={(e) => onDraftChange('b', e.target.value)}
              disabled={!ready}
              style={inputStyle}
            />
          </>
        )}
      </div>
      {teamSide(teamB)}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {locked ? (
          earned != null ? (
            <Chip tone={earned >= 3 ? 'success' : earned > 0 ? 'blue' : 'ink'}>
              {earned} PT
            </Chip>
          ) : prediction ? (
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-soft)' }}>
              {prediction.scoreA_pred}-{prediction.scoreB_pred}
            </span>
          ) : (
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-mute)' }}>
              NO PICK
            </span>
          )
        ) : (
          <Button variant="secondary" onClick={onSave} disabled={!ready || saving} style={{ padding: '6px 12px', fontSize: 12 }}>
            {saving ? '…' : justSaved ? '✓' : 'SAVE'}
          </Button>
        )}
      </div>
    </div>
  );
};

const inputStyle = {
  width: 44, padding: '6px 4px', textAlign: 'center',
  fontFamily: 'var(--font-display)', fontSize: 16,
  border: '1.5px solid var(--hairline-strong)', borderRadius: 6,
  background: 'var(--paper)', color: 'var(--ink)', outline: 'none',
};
